import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import {
    ReloadOutlined,
    PlusCircleFilled,
    MinusCircleFilled,
    CloseCircleFilled,
} from '@ant-design/icons';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { useTranslation } from 'react-i18next';
import './AccessibilityToolbar.css';

// Custom Contrast Mode Icon - Half-filled circle representing light/dark contrast
const ContrastIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        fill="none"
        style={style}
        className="contrast-icon"
    >
        <circle cx="12" cy="12" r="12" fill="white" />
        <circle
            cx="12"
            cy="12"
            r="6.25"
            stroke="#1D1D1B"
            strokeWidth="1.5"
            fill="none"
        />
        <path
            d="M12 5C15.866 5 19 8.13401 19 12C19 15.866 15.866 19 12 19V5Z"
            fill="#1D1D1B"
        />
    </svg>
);

interface AccessibilitySettings {
    fontSize: number;
    contrast: 'normal' | 'dark';
}

const AccessibilityToolbar: React.FC = () => {
    const { isToolbarVisible, hideToolbar } = useAccessibility();
    const { t } = useTranslation();

    // Load settings from localStorage on mount
    const [settings, setSettings] = useState<AccessibilitySettings>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('accessibility-settings');
            if (saved) {
                try {
                    return JSON.parse(saved);
                } catch (e) {
                    return { fontSize: 100, contrast: 'normal' };
                }
            }
        }
        return { fontSize: 100, contrast: 'normal' };
    });

    // Save settings to localStorage whenever they change
    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(
                'accessibility-settings',
                JSON.stringify(settings)
            );
        }
    }, [settings]);

    // Apply settings to document
    useEffect(() => {
        const root = document.documentElement;
        const body = document.body;

        // Apply font size scaling using zoom
        const scale = settings.fontSize / 100;

        if (scale === 1) {
            // Reset to default
            (body.style as any).zoom = '';
            root.style.transform = '';
            root.style.transformOrigin = '';
            root.style.width = '';
        } else {
            // Apply zoom for all browsers (Chrome, Safari, Edge)
            (body.style as any).zoom = String(scale);

            // Fallback for Firefox using transform scale
            root.style.transform = `scale(${scale})`;
            root.style.transformOrigin = 'top left';
            root.style.width = `${100 / scale}%`;
        }

        // Apply contrast mode
        root.setAttribute('data-contrast', settings.contrast);

        // Cleanup function to reset zoom if toolbar is hidden
        return () => {
            if (!isToolbarVisible) {
                (body.style as any).zoom = '';
                root.style.transform = '';
                root.style.transformOrigin = '';
                root.style.width = '';
            }
        };
    }, [settings.fontSize, settings.contrast, isToolbarVisible]);

    const handleReset = () => {
        setSettings({
            fontSize: 100,
            contrast: 'normal',
        });
    };

    const handleContrastToggle = () => {
        // Toggle between normal and dark mode only
        setSettings({
            ...settings,
            contrast: settings.contrast === 'normal' ? 'dark' : 'normal',
        });
    };

    const handleFontSizeIncrease = () => {
        setSettings(prev => ({
            ...prev,
            fontSize: Math.min(prev.fontSize + 10, 150),
        }));
    };

    const handleFontSizeDecrease = () => {
        setSettings(prev => ({
            ...prev,
            fontSize: Math.max(prev.fontSize - 10, 80),
        }));
    };

    if (!isToolbarVisible) {
        return null;
    }

    return (
        <div className="accessibility-toolbar">
            <div className="accessibility-toolbar-container">
                <div className="accessibility-toolbar-content">
                    <Button
                        type="text"
                        onClick={handleReset}
                        className="accessibility-toolbar-btn"
                    >
                        {t('accessibilityToolbar.reset')} <ReloadOutlined />
                    </Button>

                    <Button
                        type="text"
                        onClick={handleContrastToggle}
                        className="accessibility-toolbar-btn"
                    >
                        {t('accessibilityToolbar.contrast')}{' '}
                        <ContrastIcon />
                    </Button>

                    <div className="accessibility-toolbar-size-group">
                        <span className="accessibility-toolbar-label">
                            {t('accessibilityToolbar.size')}
                        </span>
                        <div className="accessibility-toolbar-size-button-group">
                            <Button
                                type="text"
                                onClick={handleFontSizeIncrease}
                                className="accessibility-toolbar-size-btn"
                                disabled={settings.fontSize >= 150}
                                title={t('accessibilityToolbar.increase_font')}
                            >
                                <PlusCircleFilled />
                            </Button>
                            <Button
                                type="text"
                                onClick={handleFontSizeDecrease}
                                className="accessibility-toolbar-size-btn"
                                disabled={settings.fontSize <= 80}
                                title={t('accessibilityToolbar.decrease_font')}
                            >
                                <MinusCircleFilled />
                            </Button>
                        </div>
                    </div>

                    <Button
                        type="text"
                        onClick={hideToolbar}
                        className="accessibility-toolbar-btn"
                    >
                        {t('accessibilityToolbar.hide_settings')}{' '}
                        <CloseCircleFilled />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AccessibilityToolbar;
